import { InfoIcon, Spin, Tooltip } from "melco-ui";
import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

export const TooltipContainer = styled.div`
  min-width: 200px;
  max-width: 240px;
  padding: 8px;
  font-size: 13px;
  line-height: 1.4;
`;

export const TooltipHeader = styled.div`
  font-weight: bold;
  margin-bottom: 4px;
`;

export const TooltipBulletItem = styled.div`
  position: relative;
  padding-left: 14px;
  font-size: 13px;
  color: #333;

  &::before {
    content: '•';
    position: absolute;
    left: 0;
    color: #000;
  }
`;

export const TooltipItem = styled.div`
  &::before {
    content: "•";
    display: inline-block;
    margin-right: 6px;
    color: #000;
  }
`;

export const TooltipTitle = styled.div`
  font-weight: bold;
  color: #000;
  margin-bottom: 4px;
  font-size: 13px;
`;

export const TooltipText = styled.div`
  color: #333;
  font-size: 13px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
`;


export const TooltipError = styled.div`
  color: red;
  font-size: 12px;
  padding: 8px;
  background-color: #fff;
  border-radius: 4px;
  text-align: center;
  min-width: 200px;
  min-height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const SpinnerWrapper = styled.div`
  min-width: 200px;
  min-height: 50px;
  padding: 8px 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  line-height: 1.4;
  background-color: #fff;
  color: #000;
  border-radius: 4px;
`;

export const ErrorWrapper = styled.div`
  min-width: 200px;
  min-height: 50px;
  padding: 8px 12px;
  font-size: 13px;
  line-height: 1.4;
  color: red;
  background-color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
`;



interface AsyncTooltipProps<TData, TParam> {
  id: string;
  param: TParam;
  isActive: boolean;
  onToggle: (id: string | null) => void;
  useDataHook: (param: TParam, enabled: boolean) => {
    data: TData | undefined;
    isLoading: boolean;
    error: unknown;
  };
  closeOnClickOutside?: boolean
  renderContent?: (data: TData | undefined) => React.ReactNode;
}


export function AsyncTooltip<TData, TParam>({
  id,
  param,
  isActive,
  onToggle,
  useDataHook,
  renderContent,
  closeOnClickOutside = true,
}: AsyncTooltipProps<TData, TParam>) {
  const { data, isLoading, error } = useDataHook(param, isActive);
  const triggerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation()

  // Close tooltip when clicking outside
  useEffect(() => {
    if (!isActive || !closeOnClickOutside) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        onToggle(null); // close all tooltips
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isActive, closeOnClickOutside]);

  const renderTooltipContent = () => {
    if (isLoading) {
      return (
        <SpinnerWrapper>
          <Spin size="small" />
        </SpinnerWrapper>
      );
    }

    if (error) {
      return <ErrorWrapper>{t("global.error_loading_data")}</ErrorWrapper>;
    }

    if (!data) {
      return null;
    }

    // Only call custom renderContent when data is loaded
    return renderContent ? renderContent(data) : <pre>{JSON.stringify(data)}</pre>;
  };

  return (
    <Tooltip
      title={renderTooltipContent()}
      visible={isActive}
      placement="top"
      destroyTooltipOnHide
      overlayInnerStyle={{
        backgroundColor: "#fff",
        color: "#000",
        borderRadius: 8,
        padding: 0,
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
      }}
    >
      <div
        ref={triggerRef}
        onClick={(e) => {
          e.stopPropagation();
          onToggle(id); // parent decides open/close
        }}
        style={{ cursor: "pointer" }}
      >
        <InfoIcon />
      </div>
    </Tooltip>
  );
}

